import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { ShareState } from "../types";

// Initial state
const initialState: ShareState = {
  users: [],
  departments: [],
  loading: true,
  error: "",
  isError: false,
};

// Async thunk for getting user and department dropdown
// Note: This will need to be configured by the consuming application
export const getAllUserAndDepartmentDropdown = createAsyncThunk(
  "share/getUser",
  async (_, { extra }: any) => {
    // The consuming application should provide the API client in the extra field
    if (!extra?.apiClient?.getV1PagesUserList) {
      throw new Error("API client not configured for share slice");
    }
    const response = await extra.apiClient.getV1PagesUserList();
    return response;
  },
);

export const sharePage = createAsyncThunk(
  "share/sharePage",
  async (data: any, { extra }: any) => {
    if (!extra?.apiClient?.postV1PagesShare) {
      throw new Error("API client not configured for share slice");
    }
    const response = await extra.apiClient.postV1PagesShare(data);
    return response;
  },
);

export const shareSlice = createSlice({
  name: "share",
  initialState,
  reducers: {
    // Synchronous actions for direct state updates
    setUsers: (state, action) => {
      state.users = action.payload;
    },
    setDepartments: (state, action) => {
      state.departments = action.payload;
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
      state.isError = !!action.payload;
    },
    resetState: (state) => {
      Object.assign(state, initialState);
    },
  },
  extraReducers: (builder) => {
    /*-------------------------------- get share drive Pending State ---------------------------------*/
    builder.addCase(getAllUserAndDepartmentDropdown.pending, (state) => {
      state.loading = true;
      state.error = "";
      state.users = [];
      state.departments = [];
      state.isError = false;
    });

    /*-------------------------------- get share drive fulfilled State ---------------------------------*/
    builder.addCase(
      getAllUserAndDepartmentDropdown.fulfilled,
      (state, action) => {
        state.loading = false;
        state.users = action.payload?.data?.users?.map((user: any) => ({
          ...user,
          label: `${user.first_name} ${user.last_name}`,
          value: user.id,
        })) || [];
        state.departments = action.payload?.data?.roles?.map((role: any) => ({
          ...role,
          label: role.name,
          value: role.id,
        })) || [];
        state.error = "";
        state.isError = false;
      },
    );

    /*-------------------------------- get share drive Rejected State ---------------------------------*/
    builder.addCase(
      getAllUserAndDepartmentDropdown.rejected,
      (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch users and departments";
        state.isError = true;
      },
    );

    /*-------------------------------- share page actions ---------------------------------*/
    builder.addCase(sharePage.pending, (state) => {
      state.loading = true;
      state.error = "";
      state.isError = false;
    });

    builder.addCase(sharePage.fulfilled, (state) => {
      state.loading = false;
      state.error = "";
      state.isError = false;
    });

    builder.addCase(sharePage.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Failed to share page";
      state.isError = true;
    });
  },
});

export const { setUsers, setDepartments, setLoading, setError, resetState } = shareSlice.actions;

export default shareSlice.reducer;